import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { i18n } from "~/i18n"
import {
  EXPIRED_MAGIC_LINK,
  INVALID_MAGIC_LINK,
  MagicLinkError,
} from "~/pages/Auth/errors/expired-magic-link.error"

const MAGIC_LINK_ERROR_STORAGE_KEY = "login-magic-link-error"

type MagicLinkErrorCode = typeof EXPIRED_MAGIC_LINK | typeof INVALID_MAGIC_LINK

const isMagicLinkErrorCode = (
  value: string | null
): value is MagicLinkErrorCode => {
  return value === EXPIRED_MAGIC_LINK || value === INVALID_MAGIC_LINK
}

export const useMagicLinkError = () => {
  const [queryParams, setQueryParams] = useSearchParams()
  const queryError = queryParams.get("error")
  const [magicLinkError, setMagicLinkError] =
    useState<MagicLinkErrorCode | null>(null)

  useEffect(() => {
    if (isMagicLinkErrorCode(queryError)) {
      setMagicLinkError(queryError)
      window.sessionStorage.setItem(MAGIC_LINK_ERROR_STORAGE_KEY, queryError)
      return
    }

    const storedError = window.sessionStorage.getItem(
      MAGIC_LINK_ERROR_STORAGE_KEY
    )
    if (isMagicLinkErrorCode(storedError)) {
      setMagicLinkError(storedError)
      return
    }

    setMagicLinkError(null)
  }, [queryError])

  const { forwardedError, forwardedErrorTitle } = useMemo(() => {
    if (magicLinkError === EXPIRED_MAGIC_LINK) {
      const message = i18n.t("account:login.magicLink.expired.message")
      const title = i18n.t("account:login.magicLink.expired.title")
      return {
        forwardedError: new MagicLinkError(String(message)),
        forwardedErrorTitle: String(title),
      }
    }

    if (magicLinkError === INVALID_MAGIC_LINK) {
      const message = i18n.t("account:login.magicLink.invalid.message")
      const title = i18n.t("account:login.magicLink.invalid.title")
      return {
        forwardedError: new MagicLinkError(String(message)),
        forwardedErrorTitle: String(title),
      }
    }

    return {
      forwardedError: null,
      forwardedErrorTitle: undefined,
    }
  }, [magicLinkError])

  const clearMagicLinkError = (): void => {
    setMagicLinkError(null)
    window.sessionStorage.removeItem(MAGIC_LINK_ERROR_STORAGE_KEY)
    setQueryParams((previousParams) => {
      previousParams.delete("error")
      return previousParams
    })
  }

  return {
    clearMagicLinkError,
    forwardedError,
    forwardedErrorTitle,
  }
}
